import React, { useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
    Expander,
    ExpanderTheme,
    ExpanderButtonStyle,
    Panel,
    PanelTheme,
    ExpanderExpandedHeaderTheme,
    PanelBorderStyle,
    PanelBorderRadius,
    renderCustomCountExpanderHeader,
    renderCustomIconExpanderHeader,
    ExpanderBorderRoundingStyle,
    ExpanderBorderStyle,
} from '@apply/components';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { DemoSection } from './common/SectionNavigator';
import { DemoExample, DemoPage, DemoPageSection } from './common/DemoLayout';

const sections: DemoSection[] = [
    { id: 'expanderDemo_specifiedheight', title: 'Specified Height' },
    { id: 'expanderDemo_openitems', title: 'Open Items' },
    { id: 'expanderDemo_initiallyexpandedpanel', title: 'Initially Expanded Panel' },
    { id: 'expanderDemo_closeditems', title: 'Closed Items' },
    { id: 'expanderDemo_itemsofconcern', title: 'Items of Concern' },
    { id: 'expanderDemo_borderedexpander', title: 'Bordered Expander' },
    { id: 'expanderDemo_categories', title: 'Categories' },
];

export const ExpanderDemo: React.FC = () => {
    const styles = {
        contentSpacer: {
            marginLeft: 20,
            marginRight: 20,
            height: 'auto',
        },
    };

    const handleButtonCiick: React.MouseEventHandler = useCallback(() => {
        alert('Expander button clicked!');
    }, []);

    return (
        <DemoPage
            title='Expander :'
            sections={sections}
        >
            <DemoPageSection id={sections[0].id}>
                <DemoExample>
                    <Expander
                        header={renderCustomCountExpanderHeader(3, 'Expander With Specified Height')}
                        theme={ExpanderTheme.White}
                        buttonStyle={ExpanderButtonStyle.Rounded}
                        borderRoundingStyle={ExpanderBorderRoundingStyle.Rounded}
                        height='80px'
                    >
                        <div style={styles.contentSpacer}>
                            <Panel
                                theme={PanelTheme.White}
                                borderRadius={PanelBorderRadius.Small}
                                borderStyle={PanelBorderStyle.Solid}
                            >
                                This is panel 1
                            </Panel>
                            <Panel
                                theme={PanelTheme.White}
                                borderRadius={PanelBorderRadius.Small}
                                borderStyle={PanelBorderStyle.Solid}
                            >
                                This is panel 2
                            </Panel>
                            <Panel
                                theme={PanelTheme.White}
                                borderRadius={PanelBorderRadius.Small}
                                borderStyle={PanelBorderStyle.Solid}
                            >
                                This is panel 3
                            </Panel>
                        </div>
                    </Expander>
                </DemoExample>
            </DemoPageSection>
            <DemoPageSection id={sections[1].id}>
                <DemoExample>
                    <Expander
                        header={renderCustomCountExpanderHeader(3, 'Open Items')}
                        theme={ExpanderTheme.White}
                        buttonStyle={ExpanderButtonStyle.Rounded}
                        borderRoundingStyle={ExpanderBorderRoundingStyle.Rounded}
                    >
                        <div style={styles.contentSpacer}>
                            <Panel
                                theme={PanelTheme.White}
                                borderRadius={PanelBorderRadius.Small}
                                borderStyle={PanelBorderStyle.Solid}
                            >
                                This is panel 1
                            </Panel>
                            <Panel
                                theme={PanelTheme.White}
                                borderRadius={PanelBorderRadius.Small}
                                borderStyle={PanelBorderStyle.Solid}
                            >
                                This is panel 2
                            </Panel>
                            <Panel
                                theme={PanelTheme.White}
                                borderRadius={PanelBorderRadius.Small}
                                borderStyle={PanelBorderStyle.Solid}
                            >
                                This is panel 3
                            </Panel>
                        </div>
                    </Expander>
                </DemoExample>
            </DemoPageSection>
            <DemoPageSection id={sections[2].id}>
                <DemoExample>
                    <Expander
                        header={renderCustomCountExpanderHeader(1, 'Initially Expanded Panel')}
                        theme={ExpanderTheme.White}
                        buttonStyle={ExpanderButtonStyle.Rounded}
                        borderRoundingStyle={ExpanderBorderRoundingStyle.Rounded}
                        initialExpanded={true}
                    >
                        <div style={styles.contentSpacer}>
                            <div style={{ margin: '1rem 0' }}>Important things to show below.</div>
                            <Panel
                                theme={PanelTheme.White}
                                borderRadius={PanelBorderRadius.Small}
                                borderStyle={PanelBorderStyle.Solid}
                            >
                                <li>Thing 1</li>
                                <li>Thing 2</li>
                            </Panel>
                        </div>
                    </Expander>
                </DemoExample>
            </DemoPageSection>
            <DemoPageSection id={sections[3].id}>
                <DemoExample>
                    <Expander
                        header={<h3>Closed Items</h3>}
                        theme={ExpanderTheme.Blue}
                        buttonStyle={ExpanderButtonStyle.Rounded}
                        borderRoundingStyle={ExpanderBorderRoundingStyle.Rounded}
                        handleClick={handleButtonCiick}
                    >
                        <div style={styles.contentSpacer}>
                            <div style={{ margin: '1rem 0' }}>These are the children of the expander.</div>
                        </div>
                    </Expander>
                </DemoExample>
            </DemoPageSection>
            <DemoPageSection id={sections[4].id}>
                <DemoExample>
                    <Expander
                        header={renderCustomIconExpanderHeader(faExclamationTriangle, '#F93E30', 'Items of Concern')}
                        theme={ExpanderTheme.Red}
                        buttonStyle={ExpanderButtonStyle.Rounded}
                        borderRoundingStyle={ExpanderBorderRoundingStyle.Rounded}
                    >
                        <div style={styles.contentSpacer}>
                            <div style={{ margin: '1rem 0' }}>These are the children of the expander.</div>
                        </div>
                    </Expander>
                </DemoExample>
            </DemoPageSection>
            <DemoPageSection id={sections[5].id}>
                <DemoExample>
                    <Expander
                        header={<h3>Bordered Expander</h3>}
                        theme={ExpanderTheme.White}
                        buttonStyle={ExpanderButtonStyle.Rounded}
                        borderRoundingStyle={ExpanderBorderRoundingStyle.Rounded}
                        borderStyle={ExpanderBorderStyle.Solid}
                    >
                        <div style={styles.contentSpacer}>
                            <Panel
                                theme={PanelTheme.White}
                                borderStyle={PanelBorderStyle.Solid}
                                borderRadius={PanelBorderRadius.Small}
                            >
                                This is content of the panel
                            </Panel>
                            <Panel
                                theme={PanelTheme.Gray}
                                borderRadius={PanelBorderRadius.Small}
                                borderStyle={PanelBorderStyle.Solid}
                            >
                                This is some supplemental content.
                            </Panel>
                            <Panel
                                theme={PanelTheme.Yellow}
                                borderRadius={PanelBorderRadius.Small}
                            >
                                This is some content to warn or inform.
                            </Panel>
                        </div>
                    </Expander>
                </DemoExample>
            </DemoPageSection>
            <DemoPageSection id={sections[6].id}>
                <DemoExample>
                    <Expander
                        width='100%'
                        header={<h5>Category 1</h5>}
                        theme={ExpanderTheme.White}
                        buttonStyle={ExpanderButtonStyle.NoRounding}
                        borderRoundingStyle={ExpanderBorderRoundingStyle.NoRounding}
                        expandedHeaderTheme={ExpanderExpandedHeaderTheme.Blue}
                    >
                        <div style={styles.contentSpacer}>
                            <div style={{ margin: '1rem 0' }}>This is more content.</div>
                        </div>
                    </Expander>
                    <Expander
                        width='100%'
                        header={<h5>Category 2</h5>}
                        theme={ExpanderTheme.White}
                        buttonStyle={ExpanderButtonStyle.NoRounding}
                        borderRoundingStyle={ExpanderBorderRoundingStyle.NoRounding}
                        expandedHeaderTheme={ExpanderExpandedHeaderTheme.Blue}
                    >
                        <div style={styles.contentSpacer}>
                            <div style={{ margin: '1rem 0' }}>This is more content.</div>
                        </div>
                    </Expander>
                    <Expander
                        width='100%'
                        header={<h5>Category 3</h5>}
                        theme={ExpanderTheme.White}
                        buttonStyle={ExpanderButtonStyle.NoRounding}
                        borderRoundingStyle={ExpanderBorderRoundingStyle.NoRounding}
                        expandedHeaderTheme={ExpanderExpandedHeaderTheme.Blue}
                    >
                        <div style={styles.contentSpacer}>
                            <div style={{ margin: '1rem 0' }}>This is more content.</div>
                        </div>
                    </Expander>
                    <Expander
                        width='100%'
                        header={<h5>Category 4</h5>}
                        theme={ExpanderTheme.White}
                        buttonStyle={ExpanderButtonStyle.NoRounding}
                        borderRoundingStyle={ExpanderBorderRoundingStyle.NoRounding}
                        expandedHeaderTheme={ExpanderExpandedHeaderTheme.Blue}
                    >
                        <div style={styles.contentSpacer}>
                            <div style={{ margin: '1rem 0' }}>This is more content.</div>
                            <Expander
                                height='22px'
                                header={<h5>Additional Item Expander</h5>}
                                buttonStyle={ExpanderButtonStyle.NoRounding}
                                borderRoundingStyle={ExpanderBorderRoundingStyle.Rounded}
                                theme={ExpanderTheme.Gray}
                            >
                                <div style={styles.contentSpacer}>
                                    <li>Item 1</li>
                                    <li>Item 2</li>
                                    <li>Item 3</li>
                                </div>
                            </Expander>
                        </div>
                    </Expander>
                </DemoExample>
            </DemoPageSection>
        </DemoPage>
    );
};
